import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import type { CommunityFeedAnalyticsDateRange } from "@/queries/analytics/community-feed-reporting-types";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import type { CommunityFeedAnalyticsDateRangeOption } from "../community-feed/CommunityFeedAnalyticsPage";
import { CommunityFeedSentimentAnalyticsContent } from "./CommunityFeedSentimentAnalyticsContent";

/**
 * Shows the story-led sentiment view in `focused` mode and the complete analytics surface in `all` mode.
 * Defaults to `focused` when no mode is provided.
 */
export function CommunityFeedSentimentAnalyticsPage({
  mode = "focused",
}: {
  mode?: "focused" | "all";
}): React.ReactNode {
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const analyticsScope = useAnalyticsScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDateRange: CommunityFeedAnalyticsDateRange =
    searchParams.get("dateRange") === "lastYear" ? "lastYear" : "last6Months";
  const dateRangeOptions = useMemo<NonEmptyArray<CommunityFeedAnalyticsDateRangeOption>>(
    () => [
      { value: "last6Months", name: t("common.date-range.last-months", { count: 6 }) },
      { value: "lastYear", name: t("common.date-range.last-year") },
    ],
    [t],
  );
  const selectedDateRangeOption = dateRangeOptions.find((option) => option.value === selectedDateRange)!;
  const projectResult = useQuery({
    ...analyticsQueries.communityFeedReporting({
      dateRange: selectedDateRange,
      authorType: "resident",
      contentType: "post",
    }),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "project",
  });
  const portfolioResult = useQuery({
    ...analyticsQueries.portfolioCommunityFeedReporting({
      projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [],
      dateRange: selectedDateRange,
      authorType: "resident",
      contentType: "post",
    }),
    placeholderData: keepPreviousData,
    enabled: analyticsScope.type === "portfolio" && !analyticsScope.isLoading,
  });
  const { data, isLoading, isFetching } = analyticsScope.type === "portfolio" ? portfolioResult : projectResult;

  return (
    <CommunityFeedSentimentAnalyticsContent
      mode={mode}
      sentiment={data?.sentiment}
      isLoading={isLoading}
      isFetching={isFetching}
      selectedDateRange={selectedDateRangeOption}
      dateRangeOptions={dateRangeOptions}
      onDateRangeChange={(option) => setSearchParams({ dateRange: option.value }, { replace: true })}
    />
  );
}
